//NewPurchaseOrderPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import purchaseOrderAPI from '../../api/purchaseOrderAPI';
import productAPI from '../../api/productAPI';
import supplierAPI from '../../api/supplierAPI';
import storeAPI from '../../api/storeAPI';
import { formatDate } from '../../utils/dateUtils';
import ProductSearchInput from '../../components/ProductSearchInput';
import { formatNumber } from '../../utils/numberUtils';

const NewPurchaseOrderPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  
  // PO Header State
  const [poHeader, setPoHeader] = useState({
    supplier: '',
    store: '',
    expectedDeliveryDate: '',
    isCashPurchase: false,
    notes: ''
  });

  // PO Items State
  const [poItems, setPoItems] = useState([]);
  const [subtotal, setSubtotal] = useState(0);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [suppliersRes, storesRes] = await Promise.all([
          supplierAPI.getAll(),
          storeAPI.getAll()
        ]);
        setSuppliers(suppliersRes.data);
        setStores(storesRes.data);
      } catch (err) {
        setError(err.message || 'Failed to load initial data');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Calculate subtotal when items change
  useEffect(() => {
    const calculatedSubtotal = poItems.reduce(
      (sum, item) => sum + (item.quantity * item.cost), 
      0
    );
    setSubtotal(calculatedSubtotal);
  }, [poItems]);

  const handleHeaderChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPoHeader(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddProduct = (product) => {
    setPoItems(prev => {
      // Check if product already exists in PO
      const existingItemIndex = prev.findIndex(item => item.product._id === product._id);
      
      if (existingItemIndex >= 0) {
        // Update quantity if exists
        const updatedItems = [...prev];
        updatedItems[existingItemIndex].quantity += 1;
        return updatedItems;
      } else {
        // Add new item
        return [
          ...prev,
          {
            product,
            barcode: product.barcode,
            quantity: 1,
            cost: product.cost || 0,
            productName: product.name,
            productUom: product.uom || 'each',
            isReceived: false,
            receivedQuantity: 0
          }
        ];
      }
    });
  };

  const handleItemChange = (index, field, value) => {
    setPoItems(prev => {
      const updatedItems = [...prev];
      updatedItems[index][field] = field === 'quantity' || field === 'cost' 
        ? parseFloat(value) || 0 
        : value;
      return updatedItems;
    });
  };

  const handleRemoveItem = (index) => {
    setPoItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!poHeader.supplier || !poHeader.store) {
      setError('Supplier and Store are required');
      return;
    }

    if (poItems.length === 0) {
      setError('Please add at least one item');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const poData = {
        ...poHeader,
        items: poItems.map(item => ({
          productId: item.product._id,
          barcode: item.barcode,
          quantity: item.quantity,
          cost: item.cost,
          productName: item.productName,
          uom: item.productUom
        }))
      };

      const response = await purchaseOrderAPI.create(poData);
      console.log('response.data._id', response.data.po._id);
      navigate(`/purchase-order/${response.data.po._id}`);
    } catch (err) {
      setError(err.message || 'Failed to create purchase order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout pageTitle="New Purchase Order">
      <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
        <h1 className="text-2xl font-bold mb-6">New Purchase Order</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* PO Header Section */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">PO Header</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Supplier *
                </label>
                <select
                  name="supplier"
                  value={poHeader.supplier}
                  onChange={handleHeaderChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                  disabled={loading}
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map(supplier => (
                    <option key={supplier._id} value={supplier._id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Store *
                </label>
                <select
                  name="store"
                  value={poHeader.store}
                  onChange={handleHeaderChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                  disabled={loading}
                >
                  <option value="">Select Store</option>
                  {stores.map(store => (
                    <option key={store._id} value={store._id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Expected Delivery Date
                </label>
                <input
                  type="date"
                  name="expectedDeliveryDate"
                  value={poHeader.expectedDeliveryDate}
                  onChange={handleHeaderChange}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={loading}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isCashPurchase"
                  id="isCashPurchase"
                  checked={poHeader.isCashPurchase}
                  onChange={handleHeaderChange}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  disabled={loading}
                />
                <label htmlFor="isCashPurchase" className="ml-2 block text-sm text-gray-700">
                  Cash Purchase
                </label>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={poHeader.notes}
                onChange={handleHeaderChange}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                rows={2}
                disabled={loading}
              />
            </div>
          </div>

          {/* PO Items Section */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">PO Items</h2>
            
            {/* Product Search */}
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Add Products
              </label>
              <ProductSearchInput 
                onSelect={handleAddProduct}
                disabled={loading}
              />
            </div>

            {/* Items Table */}
            {poItems.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Barcode
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit Cost
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {poItems.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {item.productName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {item.productUom}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.barcode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="w-20 border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-500"
                            disabled={loading}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.cost}
                            onChange={(e) => handleItemChange(index, 'cost', e.target.value)}
                            className="w-24 border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-500"
                            disabled={loading}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {formatNumber(item.quantity * item.cost)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-600 hover:text-red-900"
                            disabled={loading}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No items added yet. Search for products above to add them to your PO.
              </div>
            )}
          </div>

          {/* PO Summary */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-end">
              <div className="w-full md:w-1/3">
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">Subtotal:</span>
                  <span>{formatNumber(subtotal)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold border-t pt-2">
                  <span>Total:</span>
                  <span>{formatNumber(subtotal)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/purchase-order')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Purchase Order'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default NewPurchaseOrderPage;